import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["canvas", "input", "clearButton", "submitButton"]
  static values = {
    lineWidth: { type: Number, default: 2 },
    strokeColor: { type: String, default: "#000000" }
  }

  connect() {
    this.canvas = this.canvasTarget
    this.ctx = this.canvas.getContext("2d")
    this.isDrawing = false
    this.hasDrawn = false
    this.lastX = 0
    this.lastY = 0

    this.setupCanvas()
    this.bindEvents()
    this.updateSubmitState()
  }

  disconnect() {
    this.unbindEvents()
  }

  setupCanvas() {
    // Set canvas size based on container, maintaining aspect ratio
    const container = this.canvas.parentElement
    const rect = container.getBoundingClientRect()
    const dpr = window.devicePixelRatio || 1

    // Set display size
    this.canvas.style.width = "100%"
    this.canvas.style.height = "150px"

    // Set actual size in memory (scaled for retina)
    this.canvas.width = rect.width * dpr
    this.canvas.height = 150 * dpr

    // Scale context to match
    this.ctx.scale(dpr, dpr)

    // Configure drawing style
    this.ctx.strokeStyle = this.strokeColorValue
    this.ctx.lineWidth = this.lineWidthValue
    this.ctx.lineCap = "round"
    this.ctx.lineJoin = "round"
  }

  bindEvents() {
    // Mouse events
    this.canvas.addEventListener("mousedown", this.startDrawing.bind(this))
    this.canvas.addEventListener("mousemove", this.draw.bind(this))
    this.canvas.addEventListener("mouseup", this.stopDrawing.bind(this))
    this.canvas.addEventListener("mouseout", this.stopDrawing.bind(this))

    // Touch events
    this.canvas.addEventListener("touchstart", this.handleTouchStart.bind(this), { passive: false })
    this.canvas.addEventListener("touchmove", this.handleTouchMove.bind(this), { passive: false })
    this.canvas.addEventListener("touchend", this.stopDrawing.bind(this))

    // Resize handler
    this.resizeHandler = this.handleResize.bind(this)
    window.addEventListener("resize", this.resizeHandler)
  }

  unbindEvents() {
    window.removeEventListener("resize", this.resizeHandler)
  }

  handleResize() {
    // Save current drawing
    const imageData = this.canvas.toDataURL()

    // Resize canvas
    this.setupCanvas()

    // Restore drawing if there was one
    if (this.hasDrawn) {
      const img = new Image()
      img.onload = () => {
        this.ctx.drawImage(img, 0, 0, this.canvas.width, this.canvas.height)
      }
      img.src = imageData
    }
  }

  getCoordinates(event) {
    const rect = this.canvas.getBoundingClientRect()
    const scaleX = this.canvas.width / rect.width / (window.devicePixelRatio || 1)
    const scaleY = this.canvas.height / rect.height / (window.devicePixelRatio || 1)

    if (event.touches && event.touches[0]) {
      return {
        x: (event.touches[0].clientX - rect.left) * scaleX,
        y: (event.touches[0].clientY - rect.top) * scaleY
      }
    }

    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY
    }
  }

  startDrawing(event) {
    this.isDrawing = true
    const coords = this.getCoordinates(event)
    this.lastX = coords.x
    this.lastY = coords.y
  }

  draw(event) {
    if (!this.isDrawing) return

    const coords = this.getCoordinates(event)

    this.ctx.beginPath()
    this.ctx.moveTo(this.lastX, this.lastY)
    this.ctx.lineTo(coords.x, coords.y)
    this.ctx.stroke()

    this.lastX = coords.x
    this.lastY = coords.y

    if (!this.hasDrawn) {
      this.hasDrawn = true
      // Update input value immediately and notify other controllers
      this.updateInputValue()
    }
  }

  stopDrawing() {
    if (this.isDrawing) {
      this.isDrawing = false
      this.updateInputValue()
    }
  }

  handleTouchStart(event) {
    event.preventDefault()
    this.startDrawing(event)
  }

  handleTouchMove(event) {
    event.preventDefault()
    this.draw(event)
  }

  clear() {
    const dpr = window.devicePixelRatio || 1
    this.ctx.clearRect(0, 0, this.canvas.width / dpr, this.canvas.height / dpr)
    this.hasDrawn = false
    this.updateInputValue()
    this.updateSubmitState()
  }

  updateInputValue() {
    if (this.hasInputTarget) {
      if (this.hasDrawn) {
        this.inputTarget.value = this.canvas.toDataURL("image/png")
      } else {
        this.inputTarget.value = ""
      }
    }

    // Update clear button state
    if (this.hasClearButtonTarget) {
      this.clearButtonTarget.disabled = !this.hasDrawn
    }

    // Dispatch event for other controllers (e.g., contract-form) to react
    this.element.dispatchEvent(new CustomEvent("signature-changed", { bubbles: true }))
  }

  updateSubmitState() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = !this.hasDrawn
    }

    if (this.hasClearButtonTarget) {
      this.clearButtonTarget.disabled = !this.hasDrawn
    }
  }

  // Called before form submission to ensure signature data is captured
  captureSignature() {
    this.updateInputValue()
  }

  isEmpty() {
    return !this.hasDrawn
  }
}
